import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Brain } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "xea_brain_last_seen_total";

/**
 * Bell-area pill that lights up whenever the XEA Brain has learned new
 * things since the user last opened the Brain Panel. Click → navigates
 * to /dashboard/profile (where the Brain Panel lives) and records the
 * current total in localStorage so the badge clears.
 *
 * Renders only when delta > 0. Otherwise null.
 */
export default function BrainBellBadge() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [delta, setDelta] = useState(0);
  const [currentTotal, setCurrentTotal] = useState(0);

  useEffect(() => {
    if (!token) return undefined;
    let cancel = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/brain/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancel) return;
        const total = r.data?.total_events || 0;
        const lastSeen = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
        setCurrentTotal(total);
        setDelta(Math.max(0, total - lastSeen));
      } catch (_e) { /* silent */ }
    };
    load();
    const id = setInterval(load, 60000);   // refresh once a minute
    return () => { cancel = true; clearInterval(id); };
  }, [token]);

  if (!token || delta <= 0) return null;

  const handleClick = () => {
    localStorage.setItem(STORAGE_KEY, String(currentTotal));
    setDelta(0);
    navigate("/dashboard/profile#brain");
  };

  return (
    <AnimatePresence>
      <motion.button
        type="button"
        onClick={handleClick}
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.85 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-pink-500 to-violet-500 px-2.5 py-1 shadow-md hover:shadow-lg transition-shadow text-white"
        data-testid="brain-bell-badge"
        title={`XEA learned ${delta} new thing${delta === 1 ? "" : "s"} about you`}
      >
        <Brain className="w-3.5 h-3.5" />
        <span className="text-[10px] font-black uppercase tracking-[0.10em] tabular-nums">
          {delta > 9 ? "9+" : delta} new
        </span>
      </motion.button>
    </AnimatePresence>
  );
}
